import { useEffect, useMemo, useRef } from 'react';
import { GoogleMap, Marker, Circle, Polyline, useJsApiLoader } from '@react-google-maps/api';
import type { TrackedResident, ResidentStatus, TrackedLatest, LatLng } from '@/shared/firebase/types';
import type { EditingGeofence } from './ResidentDetailPanel';

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string;
const DEFAULT_CENTER = { lat: 40.4168, lng: -3.7038 };
const LIBRARIES: ('places')[] = ['places'];

const STATUS_COLOR: Record<ResidentStatus, string> = {
  inside: '#2E7D32',
  outside: '#E65100',
  sos: '#C62828',
  offline: '#757575',
};

type Item = { resident: TrackedResident; status: ResidentStatus };

type Props = {
  items: Item[];
  selectedUid: string | null;
  onSelect: (uid: string) => void;
  editingGeofence: EditingGeofence | null;
  onEditingGeofenceChange: (g: EditingGeofence) => void;
  history: TrackedLatest[];
};

export function MapPanel({
  items, selectedUid, onSelect,
  editingGeofence, onEditingGeofenceChange, history,
}: Props) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAPS_KEY,
    libraries: LIBRARIES,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const fittedRef = useRef(false);

  const validItems = useMemo(
    () => items.filter((i) => isValidLatLng(i.resident.latest)),
    [items],
  );

  const initialCenter = useMemo(() => {
    const first = validItems[0]?.resident.latest;
    return first ? { lat: first.lat, lng: first.lng } : DEFAULT_CENTER;
  }, [validItems]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || fittedRef.current || validItems.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    validItems.forEach(({ resident }) => {
      if (resident.latest) bounds.extend({ lat: resident.latest.lat, lng: resident.latest.lng });
    });
    mapRef.current.fitBounds(bounds, 80);
    if (validItems.length === 1) mapRef.current.setZoom(17);
    fittedRef.current = true;
  }, [isLoaded, validItems]);

  // when entering edit mode, center on the editing geofence
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !editingGeofence) return;
    mapRef.current.panTo(editingGeofence.center);
  }, [isLoaded, editingGeofence]);

  if (loadError) {
    return (
      <div className="w-full h-full rounded-2xl bg-danger-50 text-danger p-6 grid place-items-center text-sm">
        No se pudo cargar Google Maps. Revisa la clave o las APIs habilitadas.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-full rounded-2xl bg-white shadow-soft border border-slate-100 grid place-items-center text-sm text-slate-500">
        Cargando mapa…
      </div>
    );
  }

  const selected = validItems.find((i) => i.resident.uid === selectedUid);

  return (
    <div className="w-full h-full rounded-2xl overflow-hidden border border-slate-100 shadow-soft bg-white">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={initialCenter}
        zoom={16}
        onLoad={(m) => { mapRef.current = m; }}
        onUnmount={() => { mapRef.current = null; }}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
        }}
      >
        {/* Static geofences (read-only) for residents not in edit */}
        {validItems.map(({ resident, status }) =>
          resident.config?.geofence && resident.uid !== (editingGeofence ? selectedUid : null) ? (
            <Circle
              key={`gf-${resident.uid}`}
              center={resident.config.geofence.center}
              radius={resident.config.geofence.radius}
              options={{
                strokeColor: STATUS_COLOR[status],
                strokeOpacity: 0.5,
                strokeWeight: 2,
                fillColor: STATUS_COLOR[status],
                fillOpacity: 0.06,
                clickable: false,
              }}
            />
          ) : null,
        )}

        {/* Markers — hide when editing geofence to reduce clutter */}
        {!editingGeofence && validItems.map(({ resident, status }) => {
          const pos = { lat: resident.latest!.lat, lng: resident.latest!.lng };
          const isSelected = selectedUid === resident.uid;
          return (
            <Marker
              key={resident.uid}
              position={pos}
              onClick={() => onSelect(resident.uid)}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: isSelected ? 14 : 11,
                fillColor: STATUS_COLOR[status],
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 3,
              }}
              zIndex={isSelected ? 999 : status === 'sos' ? 500 : 1}
            />
          );
        })}

        {/* Editing geofence: draggable, resizable circle */}
        {editingGeofence && (
          <EditableGeofence
            value={editingGeofence}
            onChange={onEditingGeofenceChange}
          />
        )}

        {/* History polyline */}
        {history.length > 1 && (
          <>
            <Polyline
              path={history.map((p) => ({ lat: p.lat, lng: p.lng }))}
              options={{
                strokeColor: '#1565C0',
                strokeOpacity: 0.85,
                strokeWeight: 4,
                geodesic: true,
              }}
            />
            <Marker
              position={{ lat: history[0].lat, lng: history[0].lng }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 7, fillColor: '#0D47A1', fillOpacity: 1,
                strokeColor: '#FFFFFF', strokeWeight: 2,
              }}
              title="Inicio del periodo"
            />
            <Marker
              position={{ lat: history[history.length - 1].lat, lng: history[history.length - 1].lng }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 7, fillColor: '#42A5F5', fillOpacity: 1,
                strokeColor: '#FFFFFF', strokeWeight: 2,
              }}
              title="Final del periodo"
            />
          </>
        )}

        {/* Hint when editing */}
        {editingGeofence && selected && (
          <Marker
            position={{ lat: selected.resident.latest!.lat, lng: selected.resident.latest!.lng }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8, fillColor: STATUS_COLOR[selected.status], fillOpacity: 0.9,
              strokeColor: '#FFFFFF', strokeWeight: 2,
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
}

function EditableGeofence({
  value, onChange,
}: { value: EditingGeofence; onChange: (v: EditingGeofence) => void }) {
  const circleRef = useRef<google.maps.Circle | null>(null);

  // Sync programmatic value changes (e.g. radius slider) into the live Circle
  useEffect(() => {
    const c = circleRef.current;
    if (!c) return;
    if (Math.abs(c.getRadius() - value.radius) > 0.5) c.setRadius(value.radius);
    const cc = c.getCenter();
    if (!cc || cc.lat() !== value.center.lat || cc.lng() !== value.center.lng) {
      c.setCenter(value.center);
    }
  }, [value]);

  return (
    <Circle
      center={value.center}
      radius={value.radius}
      editable
      draggable
      onLoad={(c) => { circleRef.current = c; }}
      onUnmount={() => { circleRef.current = null; }}
      onCenterChanged={() => {
        const c = circleRef.current?.getCenter();
        if (c) {
          const next: LatLng = { lat: c.lat(), lng: c.lng() };
          if (next.lat !== value.center.lat || next.lng !== value.center.lng) {
            onChange({ ...value, center: next });
          }
        }
      }}
      onRadiusChanged={() => {
        const r = circleRef.current?.getRadius();
        if (r != null && Math.abs(r - value.radius) > 0.5) {
          onChange({ ...value, radius: Math.round(r) });
        }
      }}
      options={{
        strokeColor: '#0D47A1',
        strokeOpacity: 0.9,
        strokeWeight: 3,
        fillColor: '#1565C0',
        fillOpacity: 0.15,
      }}
    />
  );
}

function isValidLatLng(p?: { lat?: unknown; lng?: unknown }): p is { lat: number; lng: number } {
  return !!p && typeof p.lat === 'number' && typeof p.lng === 'number'
    && !Number.isNaN(p.lat) && !Number.isNaN(p.lng);
}
